import { useHttpClient } from "@/composables/UseHttpClient";
import { useMutation } from "@tanstack/vue-query";
import type {
  SessionV2,
  TerritoriesAIApiV1RefreshTokenPostRequest,
  TerritoriesAIApiV1RefreshTokenPostResponse
} from "@territoriesai/types";
import type {
  MockSignInPostRequest,
  MockSignInPostResponse,
  MockResetPasswordRequest,
  MockResetPasswordResponse
} from "./mock/mock-api-types";

export const useLoginWithRefreshToken = (currentSession: SessionV2) => {
  const { request } = useHttpClient<
    TerritoriesAIApiV1RefreshTokenPostRequest,
    never
  >({
    url: "/v1/auth/refresh-token",
    method: "post"
  });

  return useMutation({
    mutationKey: ["refresh-token", currentSession.refreshToken],
    mutationFn: async (session: SessionV2) => {
      const {
        status,
        errorMessage,
        session: newSession
      } = await request<TerritoriesAIApiV1RefreshTokenPostResponse>({
        data: {
          refreshToken: session.refreshToken
        }
      });

      if (status !== "OK") {
        throw new Error(errorMessage || "Failed to refresh token");
      }

      return { session: newSession };
    }
  });
};

export const useMockSignIn = () => {
  const { request } = useHttpClient<never, SessionV2>({
    url: "/v1/sign-in",
    method: "post"
  });

  return useMutation({
    mutationKey: ["sign-in"],
    mutationFn: async (params: MockSignInPostRequest) => {
      const { status, errorMessage, session } =
        await request<MockSignInPostResponse>(
          { url: `/v1/sign-in` },
          {
            mockData: () => ({
              status: "OK",
              token: "mock-token",
              session: {
                version: 2,
                ...params,
                refreshToken: "mock-refresh-token",
                idToken: "mock-id-token",
                workspaceUserGroup: {
                  group: "ADMIN",
                  id: "mock-workspace-user-group-id",
                  workspaceId: "mock-workspace-id",
                  workspace: {
                    id: "mock-workspace-id",
                    name: "Mock Workspace",
                    credits: 500,
                    currentBillingPlanId: "FREE",
                    billingStartDate: "2025-01-01",
                    billingRenewalDate: "2026-01-01"
                  },
                  userId: "mock-user-id",
                  user: {
                    id: "mock-user-id",
                    workspaceId: "mock-workspace-id",
                    email: params.email
                  }
                }
              }
            })
          }
        );

      if (status !== "OK") {
        throw new Error(errorMessage || "Failed to sign in");
      }

      return session;
    }
  });
};

export const useMockResetPassword = () => {
  const { request } = useHttpClient<never, MockResetPasswordRequest>({
    url: "/v1/reset-password",
    method: "post"
  });

  return useMutation({
    mutationKey: ["reset-password"],
    mutationFn: async (params: MockResetPasswordRequest) => {
      const { status, errorMessage } = await request<MockResetPasswordResponse>(
        { url: `/v1/reset-password` },
        {
          mockData: () => ({
            status: "OK",
            email: params.email
          })
        }
      );

      if (status !== "OK") {
        throw new Error(errorMessage || "Failed to send reset link");
      }

      return null;
    }
  });
};
